import type { JobRoleId, ServiceId } from '@obraplan/core/domain/shared/ids';
import { Button } from '@/components/ui/button';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';

/** `0,82` e `0,5333`: duas casas, e até quatro quando a média pede — as que o "Usar" grava. */
const indice = (valor: number): string =>
  valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * A média que a obra já deu para esta função neste serviço — sugerida, nunca aplicada
 * (Fase 24, fatia 7 — D14).
 *
 * ```text
 * sugerido: 0,82 HH/m² · média de 14 dias em 3 obras      [Usar]
 * ```
 *
 * O índice da mão de obra nasce vazio, e esta linha é o que a tela oferece para preenchê-lo.
 * Oferece, e só: **nada é gravado** até o usuário clicar em "Usar" (RN-303) — o que o botão
 * faz é o mesmo que digitar o número no campo, e quem confirma é quem clica. Um índice que se
 * preenchesse sozinho trocaria uma decisão do orçamento por uma média de ontem, calado.
 *
 * Sem dados, o campo continua vazio e a linha diz o mínimo que falta (RN-300); com as
 * unidades divergentes, diz por que não sugere (RN-302). Quem não pode editar o quantitativo
 * é recusado pelo servidor e não vê a linha — a sugestão é do `takeoff.manage`, como o índice.
 */
export function SugestaoDeProdutividade({
  serviceId,
  jobRoleId,
  description,
  indiceAtual,
  onUsar,
}: {
  /** O id gravado do serviço; sem ele (ainda não voltou do servidor) não há o que perguntar. */
  serviceId: ServiceId | undefined;
  jobRoleId: JobRoleId;
  /** O nome do item, para o botão dizer de quem é o índice. */
  description: string;
  /** O índice que o item já tem — igual à sugestão, o botão não tem o que fazer. */
  indiceAtual: number | undefined;
  onUsar: (hoursPerUnit: number) => void;
}) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();

  // `async` de propósito: uma falha ao chamar — o transporte fora do ar — vira rejeição, que
  // a leitura viva já sabe tratar, e não uma exceção solta no meio da lista de itens.
  const resposta = useLive(async () => {
    if (session === undefined || activeProjectId === undefined || serviceId === undefined) {
      return undefined;
    }
    return container.getProductivitySuggestion.execute(
      { projectId: activeProjectId, serviceId, jobRoleId },
      session,
    );
  }, [session?.userId, activeProjectId, serviceId, jobRoleId]);

  // Carregando, ou recusada: nada a mostrar. A sugestão é um adorno do campo, e não pode
  // virar mensagem de erro no meio da lista de itens.
  if (resposta === undefined || !resposta.ok) return null;
  const sugestao = resposta.value;

  if (sugestao.status !== 'SUGERIDO') {
    return (
      <p data-sugestao={sugestao.status} className="mt-1 text-xs text-(--color-content-muted)">
        {sugestao.message}
      </p>
    );
  }

  const emUso = indiceAtual === sugestao.hoursPerUnit;
  const valor = `${indice(sugestao.hoursPerUnit)} HH/${sugestao.unit}`;
  const dias = `${String(sugestao.days)} dias`;
  const origem =
    sugestao.origin === 'OBRA'
      ? 'nesta obra'
      : `em ${String(sugestao.projects)} ${sugestao.projects === 1 ? 'obra' : 'obras'}`;

  return (
    <p
      data-sugestao="SUGERIDO"
      className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-(--color-content-muted)"
    >
      <span>
        sugerido: <strong className="font-medium text-(--color-content)">{valor}</strong> · média de{' '}
        {dias} {origem}
      </span>
      {emUso ? (
        <span className="text-(--color-state-ok)">em uso</span>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Usar ${valor} como índice de ${description}`}
          onClick={() => onUsar(sugestao.hoursPerUnit)}
        >
          Usar
        </Button>
      )}
    </p>
  );
}
